import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUserPreference } from '@/hooks/useUserPreference';
import { useAuthStore } from '@/store/authStore';
import { useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getPreferredClient } from '@/lib/auth';
import { markWelcomeDismissed, hasWelcomeDismissed } from '@/lib/onboardingTours/driver';
import { HELP_CENTER_LINKS } from '@/lib/onboardingTours/helpCenterLinks';

type ModalState = 'welcome' | 'confirmSkip';

// Expose testing helper in browser console
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).resetOnboardingForUser = async () => {
    try {
      // Reset onboarding_required to trigger tours again
      await getPreferredClient().post('/api/UserPreferences', {
        key: 'onboarding_required',
        value: 'true',
      });
      // Reset onboarding_state to clear all progress
      await getPreferredClient().post('/api/UserPreferences', {
        key: 'onboarding_state',
        value: JSON.stringify({ welcomeDismissed: false, completed: {} }),
      });

      // Clear localStorage cache to ensure welcome modal shows
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('watchful-onboarding-state-')) {
          localStorage.removeItem(key);
        }
      });

      console.log(
        '[Onboarding] onboarding_required and onboarding_state reset. Refresh the page to see the welcome modal.'
      );
      return true;
    } catch (error) {
      console.error('[Onboarding] Failed to reset onboarding:', error);
      return false;
    }
  };
}

export function OnboardingWelcomeModal() {
  const [modalState, setModalState] = useState<ModalState>('welcome');
  const [welcomeDismissedState, setWelcomeDismissedState] = useState(false);
  const location = useLocation();
  const { user } = useAuthStore();

  // Check if onboarding is required for this user
  // - New users: set to 'true' when created by backend
  // - Existing users: not set, defaults to 'false'
  // - Users who dismissed/completed: set to 'false'
  const {
    value: onboardingRequired,
    setValue: setOnboardingRequired,
    isLoading,
  } = useUserPreference({
    key: 'onboarding_required',
    defaultValue: 'false',
  });

  // Check if welcome modal has been dismissed from localStorage cache
  useEffect(() => {
    if (!user?.id) {
      return;
    }
    const dismissed = hasWelcomeDismissed(user.id);
    setWelcomeDismissedState(dismissed);
  }, [user?.id]);

  // Only show on homepage
  const isHomepage =
    location.pathname === '/' ||
    location.pathname === '/home' ||
    location.pathname === '/dashboard';

  const isOpen =
    !isLoading &&
    onboardingRequired === 'true' &&
    isHomepage &&
    !!user?.id &&
    !welcomeDismissedState;

  const handleGetStarted = async () => {
    if (!user?.id) {
      return;
    }

    // Mark welcome as dismissed in onboarding_state JSON blob
    await markWelcomeDismissed(user.id);
    setWelcomeDismissedState(true);
    window.dispatchEvent(new CustomEvent('onboarding-welcome-dismissed'));
  };

  const handleSkip = () => {
    setModalState('confirmSkip');
  };

  const handleConfirmSkip = async () => {
    if (!user?.id) {
      return;
    }

    // Mark welcome as dismissed in onboarding_state JSON blob
    await markWelcomeDismissed(user.id);
    setWelcomeDismissedState(true);

    // Disable onboarding_required permanently
    // Note: announcements are suppressed for new users via useAnnouncementDelay hook
    // (24-hour delay after onboarding completes), so no need to mass-dismiss here.
    setOnboardingRequired('false');
  };

  const handleGoBack = () => {
    setModalState('welcome');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        closable={false}
        showCloseButton={false}
        className="max-w-4xl overflow-hidden rounded-3xl border-2 border-lime/80 bg-[#111] p-0 shadow-[0_0_60px_rgba(209,245,81,0.25)]"
      >
        {modalState === 'welcome' ? (
          <div className="relative flex min-h-[400px]">
            {/* Left content */}
            <div className="flex flex-1 flex-col justify-center p-12">
              <h1 className="mb-6 text-5xl font-bold italic text-white">Welcome to Watchful</h1>
              <p className="mb-4 text-white/80">
                We&apos;ve prepared a guided tour to help you get familiar with the platform. In
                just a few minutes, you’ll see the key features on each page.
              </p>
              <p className="mb-10 text-white/80">
                For more details about our platform, please visit the{' '}
                <a
                  href={HELP_CENTER_LINKS.helpCentreOverview}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-lime underline hover:text-white"
                >
                  Help Center
                </a>
                .
              </p>
              <div className="flex gap-4">
                <Button onClick={handleGetStarted} className="px-8">
                  Get Started
                </Button>
                <Button variant="outline" onClick={handleSkip} className="px-8">
                  Skip
                </Button>
              </div>
            </div>

            {/* Right logo */}
            <div className="relative flex w-[45%] items-center justify-center">
              <img
                src="/icon/logo-dark.svg"
                alt="Watchful"
                className="h-[85%] w-auto max-w-none translate-x-[15%]"
              />
            </div>
          </div>
        ) : (
          <div className="relative flex min-h-[400px]">
            {/* Left content */}
            <div className="flex flex-1 flex-col justify-center p-12">
              <h1 className="mb-6 text-4xl font-bold italic text-white">Skip the Tour?</h1>
              <p className="mb-4 text-white/80">Prefer to explore on your own?</p>
              <p className="mb-4 text-white/80">No problem!</p>
              <p className="mb-16 text-lime">
                You can restart the tour anytime by clicking{' '}
                <span className="font-bold text-lg text-white">Restart Page Tour</span> in the shift dropdown menu.
              </p>
              <div className="flex gap-4">
                <Button variant="outline" onClick={handleGoBack} className="px-8">
                  <ArrowLeft className="h-4 w-4" />
                  Go Back
                </Button>
                <Button onClick={handleConfirmSkip} className="px-8">
                  Yes, Skip Tour
                </Button>
              </div>
            </div>

            {/* Right logo */}
            <div className="relative flex w-[45%] items-center justify-center">
              <img
                src="/icon/logo-dark.svg"
                alt="Watchful"
                className="h-[85%] w-auto max-w-none translate-x-[15%]"
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
